import { useState, useEffect, useRef } from "react";
import { useProducerTimeFormatter } from "@/hooks/useTimeFormatter";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sparkles, Plus, X, AlertTriangle, Search } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { stagesForDay, type BriefingStageRecord } from "@/lib/briefingStages";
import { filterArtistsBySearch } from "@/lib/artistSearchFilter";
import { buildGenerateScheduleBody } from "@/lib/aiScheduleRequest";
import { rowMatchesStage } from "@produce/shared";
import { AiProgressDialog } from "./AiProgressDialog";
import { AiErrorDialog } from "./AiErrorDialog";
import type { AiGenerationSettings, AiConflict } from "@/types/domain";

interface CrewMember {
  id?: string;
  name: string;
  function: string;
  color?: string;
}

interface Artist {
  id?: string;
  artist_name: string;
  stage_id?: string | null;
  stage_name: string;
  set_start: string;
  set_end: string;
  event_date: string;
}

// Stable identity key for "this artist on this lineup slot". When
// the lineup row carries an id (UUID FK target) we key on it —
// rename of stage or artist text doesn't fragment the activated set.
// Falls back to the (artist_name, stage_name) tuple for legacy rows
// loaded without their id (rare; AI dialog usually has fresh data).
const artistKey = (a: Pick<Artist, "id" | "artist_name" | "stage_id" | "stage_name">): string => {
  if (a.id) return `id:${a.id}`;
  if (a.stage_id) return `stage:${a.stage_id}::${a.artist_name}`;
  return `name:${a.artist_name}-${a.stage_name}`;
};

interface Assignment {
  id?: string;
  crew_member_id: string;
  event_date: string;
  artist_name?: string;
  stage_name: string;
  filming_start: string;
  filming_end: string;
  assignment_type: "artist" | "custom";
  subject?: string;
  location?: string;
  notes?: string;
}

interface CustomSubject {
  subject: string;
  location: string;
  stage_id?: string | null;
  frequency: number;
  duration: number;
  preferredTimeStart?: string;
  preferredTimeEnd?: string;
  preferredCrewMember?: string;
}

interface AiGenerationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  crew: CrewMember[];
  artists: Artist[];
  stages: Array<{ name: string; label: string }>;
  selectedDate: string;
  briefingId: string;
  existingAssignments: Assignment[];
  onAssignmentsGenerated: (newAssignments: Assignment[]) => void;
  onSettingsSaved?: (settings: AiGenerationSettings) => void;
  previousAiSettings?: {
    slotMinutes?: number;
    defaultDuration?: number;
    crewBreaks?: Array<{ time: string; duration: number }>;
    customSubjects?: CustomSubject[];
    activatedCrew?: Set<string>;
    activatedArtists?: Set<string>;
    crewPreferredStages?: Record<string, string[]>;
    travelTime?: number;
    regenerateInstructions?: string;
  };
  autoGenerate?: boolean;
}

export function AiGenerationDialog({
  open,
  onOpenChange,
  crew,
  artists,
  stages,
  selectedDate,
  briefingId,
  existingAssignments,
  onAssignmentsGenerated,
  onSettingsSaved,
  previousAiSettings,
  autoGenerate = false,
}: AiGenerationDialogProps) {
  const [activatedCrew, setActivatedCrew] = useState<Set<string>>(new Set());
  const [crewPreferredStages, setCrewPreferredStages] = useState<Record<string, string[]>>({});
  const [activatedArtists, setActivatedArtists] = useState<Set<string>>(new Set());
  const [artistSearch, setArtistSearch] = useState("");
  const artistSearchRef = useRef<HTMLInputElement>(null);
  const [customSubjects, setCustomSubjects] = useState<CustomSubject[]>([]);
  const [defaultDuration, setDefaultDuration] = useState<string>("15");
  const [travelTime, setTravelTime] = useState(15);
  const [crewBreaks, setCrewBreaks] = useState<Array<{ time: string; duration: string }>>([
    { time: "12:00", duration: "60" },
  ]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showProgressDialog, setShowProgressDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorSuggestions, setErrorSuggestions] = useState<string[]>([]);
  const [errorConflicts, setErrorConflicts] = useState<AiConflict[]>([]);

  // Filter stages to only show those relevant for the selected day
  // (remove duplicates). FK-aware: rowMatchesStage prefers stage_id
  // when both sides have it so a producer rename mid-dialog can't
  // strand a stage from the relevant set.
  const relevantStages = stages.filter((stage, index, self) => {
    const hasArtists = artists.some((artist) => rowMatchesStage(artist, stage));
    const isFirstOccurrence = self.findIndex((s) => s.label === stage.label) === index;
    return hasArtists && isFirstOccurrence;
  });

  // Canonical briefing_stages — used to populate the per-custom-subject
  // stage dropdown so a "Publiek op Mainstage" subject carries the
  // real stage_id all the way through to the AI's output.
  const { data: briefingStageRecords = [] } = useQuery({
    queryKey: ["briefing_stages", briefingId],
    enabled: !!briefingId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("briefing_stages")
        .select("*")
        .eq("briefing_id", briefingId)
        .order("display_order", { ascending: true });
      if (error) throw error;
      return (data ?? []) as BriefingStageRecord[];
    },
  });
  const stagesForSelectedDay = stagesForDay(briefingStageRecords, selectedDate);

  // Initialize all artists as activated only when dialog opens, not when artists prop changes while open
  const prevOpen = useRef(false);
  useEffect(() => {
    if (open && !prevOpen.current) {
      setArtistSearch("");
    }
    if (open && !prevOpen.current && artists.length > 0 && !autoGenerate) {
      const allArtistKeys = new Set(artists.map((a) => artistKey(a)));
      setActivatedArtists(allArtistKeys);
    }
    if (open && !prevOpen.current && crew.length > 0 && !autoGenerate) {
      const allCrewIds = new Set(crew.map((c) => c.id).filter((id): id is string => !!id));
      setActivatedCrew(allCrewIds);
    }
    prevOpen.current = open;
  }, [open, artists, crew, autoGenerate]);

  // Load previous settings when dialog opens with autoGenerate
  useEffect(() => {
    if (open && autoGenerate) {
      if (previousAiSettings) {
        // Restore previous settings with fallbacks for empty values
        if (previousAiSettings.activatedCrew && previousAiSettings.activatedCrew.size > 0) {
          setActivatedCrew(previousAiSettings.activatedCrew);
        } else {
          const allCrewIds = new Set(crew.map((c) => c.id).filter((id): id is string => !!id));
          setActivatedCrew(allCrewIds);
        }

        if (previousAiSettings.activatedArtists && previousAiSettings.activatedArtists.size > 0) {
          setActivatedArtists(previousAiSettings.activatedArtists);
        } else {
          const allArtistKeys = new Set(artists.map((a) => artistKey(a)));
          setActivatedArtists(allArtistKeys);
        }

        if (previousAiSettings.crewPreferredStages) {
          setCrewPreferredStages(previousAiSettings.crewPreferredStages);
        }
        if (previousAiSettings.customSubjects) {
          setCustomSubjects(previousAiSettings.customSubjects);
        }
        if (previousAiSettings.defaultDuration) {
          setDefaultDuration(previousAiSettings.defaultDuration.toString());
        }
        if (previousAiSettings.travelTime !== undefined) {
          setTravelTime(previousAiSettings.travelTime);
        }
        if (previousAiSettings.crewBreaks) {
          setCrewBreaks(
            previousAiSettings.crewBreaks.map((b: { time: string; duration: number }) => ({
              time: b.time,
              duration: b.duration.toString(),
            })),
          );
        }
      } else {
        // No previous settings - initialize with defaults
        const allCrewIds = new Set(crew.map((c) => c.id).filter((id): id is string => !!id));
        setActivatedCrew(allCrewIds);
        const allArtistKeys = new Set(artists.map((a) => artistKey(a)));
        setActivatedArtists(allArtistKeys);
      }
    }
  }, [open, autoGenerate, previousAiSettings, crew, artists]);

  const toggleCrewActivation = (crewId: string) => {
    setActivatedCrew((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(crewId)) {
        newSet.delete(crewId);
        const newPrefs = { ...crewPreferredStages };
        delete newPrefs[crewId];
        setCrewPreferredStages(newPrefs);
      } else {
        newSet.add(crewId);
      }
      return newSet;
    });
  };

  const toggleCrewStage = (crewId: string, stage: string) => {
    setCrewPreferredStages((prev) => {
      const currentStages = prev[crewId] || [];

      // Reset to "no preference" (empty array)
      // Also clears "no_stage" if it was previously selected
      if (stage === "no_preference") {
        return { ...prev, [crewId]: [] };
      }

      if (stage === "no_stage") {
        return { ...prev, [crewId]: ["no_stage"] };
      }

      // If "no_stage" is currently selected OR no preference, replace it with the new stage
      if (currentStages.includes("no_stage") || currentStages.length === 0) {
        return { ...prev, [crewId]: [stage] };
      }

      // Toggle the stage
      if (currentStages.includes(stage)) {
        return { ...prev, [crewId]: currentStages.filter((s) => s !== stage) };
      } else {
        return { ...prev, [crewId]: [...currentStages, stage] };
      }
    });
  };

  const toggleArtistActivation = (artist: Artist) => {
    const key = artistKey(artist);
    // Derive the next state from the current render's set (a toggle is a
    // pure function of current state) so the updater stays side-effect free.
    const willActivate = !activatedArtists.has(key);
    setActivatedArtists((prev) => {
      const newSet = new Set(prev);
      if (willActivate) newSet.add(key);
      else newSet.delete(key);
      return newSet;
    });
    // After checking an artist found via search, clear the query and
    // refocus the field so the producer can immediately type the next
    // name without having to delete the previous text first.
    if (willActivate && artistSearch !== "") {
      setArtistSearch("");
      artistSearchRef.current?.focus();
    }
  };

  // Activate / deactivate every artist currently visible under the
  // search query, leaving artists outside the current filter untouched.
  const setVisibleArtistsActivation = (activate: boolean) => {
    setActivatedArtists((prev) => {
      const next = new Set(prev);
      for (const a of filteredArtists) {
        if (activate) next.add(artistKey(a));
        else next.delete(artistKey(a));
      }
      return next;
    });
  };

  const addCustomSubject = () => {
    setCustomSubjects((prev) => [
      ...prev,
      {
        subject: "",
        location: "",
        stage_id: null,
        frequency: 1,
        duration: 15,
        preferredTimeStart: "",
        preferredTimeEnd: "",
        preferredCrewMember: "",
      },
    ]);
  };

  const removeCustomSubject = (index: number) => {
    setCustomSubjects((prev) => prev.filter((_, i) => i !== index));
  };

  const updateCustomSubject = (
    index: number,
    field: keyof CustomSubject,
    value: string | number,
  ) => {
    setCustomSubjects((prev) => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
  };

  const handleGenerateClick = () => {
    if (activatedCrew.size === 0) {
      toast.error("Please activate at least one crew member");
      return;
    }

    // Check if all activated crew members have valid IDs
    const crewWithoutIds = Array.from(activatedCrew).filter((crewId) => {
      const member = crew.find((c) => c.id === crewId);
      return !member || !member.id;
    });

    if (crewWithoutIds.length > 0) {
      toast.error(
        "Please save the crew members first before generating AI schedule. Click 'Save Draft' or 'Save & Publish' button.",
      );
      return;
    }

    if (activatedArtists.size === 0 && customSubjects.length === 0) {
      toast.error("Please activate at least one artist or add a custom subject");
      return;
    }

    setShowConfirmDialog(true);
  };

  const handleGenerate = async () => {
    setShowConfirmDialog(false);
    setIsGenerating(true);
    setShowProgressDialog(true);
    setGenerationProgress(10);
    onOpenChange(false); // Close the AI dialog

    try {
      setGenerationProgress(30);

      // Validate briefingId before proceeding
      if (!briefingId || briefingId === "create") {
        toast.error("Please save the briefing first before generating AI schedule.");
        return;
      }

      // Prepare input data - filter out crew without IDs (safety check)
      const crewData = Array.from(activatedCrew)
        .map((crewId) => {
          const member = crew.find((c) => c.id === crewId);
          const preferredStages = crewPreferredStages[crewId] || [];
          return {
            id: crewId,
            name: member?.name || "",
            function: member?.function || "",
            preferredStages: preferredStages.filter((s) => s !== "no_stage"),
            noStage: preferredStages.includes("no_stage"),
            activated: true,
          };
        })
        .filter((c) => c.id && c.id.length > 0); // Filter out any crew without valid IDs

      if (crewData.length === 0) {
        toast.error("No valid crew members found. Please save the briefing first.");
        return;
      }

      const artistsData = artists
        .filter((a) => {
          const key = artistKey(a);
          return activatedArtists.has(key);
        })
        .map((a) => ({
          name: a.artist_name,
          stage: a.stage_name,
          start: a.set_start,
          end: a.set_end,
          activated: true,
        }));

      const validCustomSubjects = customSubjects.filter((s) => s.subject.trim() !== "");

      setGenerationProgress(50);

      const { data, error } = await supabase.functions.invoke("generate-crew-schedule", {
        body: buildGenerateScheduleBody({
          briefingId,
          date: selectedDate,
          defaultDuration: parseInt(defaultDuration),
          travelTime,
          crew: crewData,
          artists: artistsData,
          // Available stages on this date, with UUIDs. The edge function
          // uses these to resolve each assignment's canonical stage_id.
          briefingStages: stagesForSelectedDay.map((s) => ({ id: s.id, label: s.label })),
          customSubjects: validCustomSubjects,
          crewBreaks: crewBreaks.map((b) => ({ time: b.time, duration: parseInt(b.duration) })),
          existingAssignments,
          regenerateInstructions: previousAiSettings?.regenerateInstructions || undefined,
        }),
      });

      setGenerationProgress(80);

      if (error) {
        console.error(error);
        setErrorMessage("The AI encountered an error while generating the schedule.");
        setErrorSuggestions([
          "Try reducing the number of activated artists",
          "Check if there are enough crew members for all assignments",
          "Ensure break times don't conflict with critical assignments",
          "Try simplifying custom subjects",
        ]);
        setShowErrorDialog(true);
        return;
      }

      if (data?.error) {
        setErrorMessage(data.error);
        setErrorConflicts(data.conflicts || []);
        setErrorSuggestions(
          data.suggestions || [
            "Try reducing the number of activated artists",
            "Add more crew members to the selected day",
            "Adjust break times to provide more scheduling flexibility",
          ],
        );
        setShowErrorDialog(true);
        return;
      }

      if (data?.assignments) {
        setGenerationProgress(100);
        await new Promise((resolve) => setTimeout(resolve, 500)); // Show 100% briefly

        // Validate that all crew_member_ids exist in the crew list
        const validCrewIds = new Set(crew.map((c) => c.id).filter((id): id is string => !!id));
        const validAssignments = data.assignments.filter((a: Assignment) =>
          validCrewIds.has(a.crew_member_id),
        );

        // Log any invalid assignments for debugging
        const invalidAssignments = data.assignments.filter(
          (a: Assignment) => !validCrewIds.has(a.crew_member_id),
        );
        if (invalidAssignments.length > 0) {
          toast.warning(
            `${invalidAssignments.length} assignment(s) were skipped due to invalid crew references`,
          );
        }

        // Save current settings for future regeneration
        if (onSettingsSaved) {
          onSettingsSaved({
            slotMinutes: 15,
            defaultDuration: parseInt(defaultDuration),
            crewBreaks: crewBreaks.map((b) => ({ time: b.time, duration: parseInt(b.duration) })),
            customSubjects,
            activatedCrew,
            activatedArtists,
            crewPreferredStages,
            travelTime,
          });
        }

        // Don't deactivate artists - keep all artists visible in the schedule
        onAssignmentsGenerated(validAssignments);
        toast.success("AI plan generated successfully");
      } else {
        setErrorMessage("No assignments were generated by the AI.");
        setErrorSuggestions([
          "Try activating more artists or crew members",
          "Check if the schedule configuration is valid",
        ]);
        setShowErrorDialog(true);
      }
    } catch (error) {
      console.error(error);
      setErrorMessage("An unexpected error occurred during schedule generation.");
      setErrorSuggestions([
        "Please try again",
        "If the problem persists, try with fewer activated artists",
      ]);
      setShowErrorDialog(true);
    } finally {
      setIsGenerating(false);
      setShowProgressDialog(false);
      setGenerationProgress(0);
    }
  };

  // Narrow the artist list to names starting with the producer's search
  // query (empty query = all artists). Grouping below runs on the
  // filtered set so the stage groups only show matching artists.
  const filteredArtists = filterArtistsBySearch(artists, artistSearch);

  // Group artists by stage
  const artistsByStage = filteredArtists.reduce(
    (acc, artist) => {
      if (!acc[artist.stage_name]) {
        acc[artist.stage_name] = [];
      }
      acc[artist.stage_name].push(artist);
      return acc;
    },
    {} as Record<string, Artist[]>,
  );

  // Sort stages and artists
  Object.keys(artistsByStage).forEach((stage) => {
    artistsByStage[stage].sort((a, b) => {
      const toMin = (t: string) => {
        const [h, m] = t.substring(0, 5).split(":").map(Number);
        return h < 6 ? h * 60 + m + 1440 : h * 60 + m;
      };
      return toMin(a.set_start) - toMin(b.set_start);
    });
  });

  // Format time to HH:mm (remove seconds)
  // The producer's own 12h/24h schedule setting. This screen carried a
  // private 24h copy, so the setting did nothing here.
  const formatTime = useProducerTimeFormatter();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="z-50 max-h-[90vh] max-w-4xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            Generate with AI
          </DialogTitle>
          <DialogDescription>
            Configure the AI assistant to automatically create a crew schedule
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="relative z-50 max-h-[calc(90vh-200px)] pr-4">
          <div className="space-y-6">
            {/* Activate crew members */}
            <div>
              <Label className="mb-3 block text-base font-semibold">Activate crew members</Label>
              <div className="space-y-3">
                {crew.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center gap-3 rounded-lg bg-muted/50 p-3"
                  >
                    <Checkbox
                      checked={activatedCrew.has(member.id!)}
                      onCheckedChange={() => toggleCrewActivation(member.id!)}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="font-medium">{member.name}</p>
                      <p className="text-sm text-muted-foreground">{member.function}</p>
                    </div>
                    {activatedCrew.has(member.id!) && (
                      <div className="flex flex-1 flex-col gap-2">
                        <Label className="text-sm">Preferred stages (select multiple):</Label>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            type="button"
                            variant={
                              !crewPreferredStages[member.id!]?.length ? "default" : "outline"
                            }
                            size="sm"
                            onClick={() => toggleCrewStage(member.id!, "no_preference")}
                          >
                            No preference
                          </Button>
                          <Button
                            type="button"
                            variant={
                              crewPreferredStages[member.id!]?.includes("no_stage")
                                ? "default"
                                : "outline"
                            }
                            size="sm"
                            onClick={() => toggleCrewStage(member.id!, "no_stage")}
                          >
                            No stage scheduled
                          </Button>
                          {relevantStages.map((stage) => (
                            <Button
                              key={stage.name}
                              type="button"
                              variant={
                                crewPreferredStages[member.id!]?.includes(stage.label)
                                  ? "default"
                                  : "outline"
                              }
                              size="sm"
                              onClick={() => toggleCrewStage(member.id!, stage.label)}
                              disabled={crewPreferredStages[member.id!]?.includes("no_stage")}
                            >
                              {stage.label}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Select artists to include */}
            <div>
              <div className="mb-3 flex items-center justify-between">
                <Label className="text-base font-semibold">Select artists to include</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setVisibleArtistsActivation(true)}
                  >
                    Select all
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setVisibleArtistsActivation(false)}
                  >
                    Deselect all
                  </Button>
                </div>
              </div>
              <p className="mb-3 text-sm text-muted-foreground">
                Deselect artists you don't want to include in the AI-generated schedule.
              </p>
              {artists.length === 0 ? (
                <p className="rounded-lg bg-muted/50 p-3 text-sm text-muted-foreground">
                  No artists found for this date. Make sure you've added artists to the timetable.
                </p>
              ) : (
                <>
                  {/* px-1 + ring-offset-0 keep the focus ring off the
                      ScrollArea's clipping edges (it was being shaved on
                      the left/right otherwise). */}
                  <div className="relative mb-3 px-1">
                    <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      ref={artistSearchRef}
                      value={artistSearch}
                      onChange={(e) => setArtistSearch(e.target.value)}
                      placeholder="Search artist by name..."
                      aria-label="Search artists by name"
                      className="pl-9 pr-9 focus-visible:ring-offset-0"
                    />
                    {artistSearch !== "" && (
                      <button
                        type="button"
                        onClick={() => {
                          setArtistSearch("");
                          artistSearchRef.current?.focus();
                        }}
                        aria-label="Clear search"
                        className="absolute right-3 top-1/2 -translate-y-1/2 rounded-sm p-0.5 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  {Object.entries(artistsByStage).length === 0 ? (
                    <p className="rounded-lg bg-muted/50 p-3 text-sm text-muted-foreground">
                      No artists match &ldquo;{artistSearch.trim()}&rdquo;.
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {Object.entries(artistsByStage).map(([stageName, stageArtists]) => (
                        <div key={stageName} className="rounded-lg bg-muted/50 p-3">
                          <Label className="mb-2 block text-sm font-medium">{stageName}</Label>
                          <div className="grid grid-cols-2 gap-2">
                            {stageArtists.map((artist, idx) => {
                              const key = artistKey(artist);
                              return (
                                <div key={idx} className="flex items-center gap-2">
                                  <Checkbox
                                    checked={activatedArtists.has(key)}
                                    onCheckedChange={() => toggleArtistActivation(artist)}
                                  />
                                  <span className="truncate text-sm">
                                    <span className="mr-1.5 text-muted-foreground">
                                      {formatTime(artist.set_start)}
                                    </span>
                                    {artist.artist_name}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Custom subjects */}
            <div>
              <div className="mb-3 flex items-center justify-between">
                <Label className="text-base font-semibold">Custom subjects</Label>
                <Button type="button" variant="outline" size="sm" onClick={addCustomSubject}>
                  <Plus className="mr-1 h-4 w-4" />
                  Add Subject
                </Button>
              </div>
              <p className="mb-3 text-sm text-muted-foreground">
                Custom subjects can also be added later after generating the schedule.
              </p>
              <div className="space-y-3">
                {customSubjects.map((subject, index) => (
                  <div key={index} className="space-y-2 rounded-lg bg-muted/50 p-3">
                    <div className="grid grid-cols-[2fr_2fr_1fr_1fr_auto] items-center gap-3">
                      <div>
                        <Label className="mb-1 block text-xs text-muted-foreground">
                          Subject name
                        </Label>
                        <Input
                          value={subject.subject}
                          onChange={(e) => updateCustomSubject(index, "subject", e.target.value)}
                          placeholder="e.g., B-roll, Crowd shots"
                        />
                      </div>
                      <div>
                        <Label className="mb-1 block text-xs text-muted-foreground">Stage</Label>
                        <Select
                          value={subject.stage_id ?? "__none__"}
                          onValueChange={(v) => {
                            const next = v === "__none__" ? null : v;
                            const matchedLabel = next
                              ? (briefingStageRecords.find((s) => s.id === next)?.label ?? "")
                              : "";
                            setCustomSubjects((prev) =>
                              prev.map((s, i) =>
                                i === index ? { ...s, stage_id: next, location: matchedLabel } : s,
                              ),
                            );
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select stage" />
                          </SelectTrigger>
                          <SelectContent>
                            {stagesForSelectedDay.map((s) => (
                              <SelectItem key={s.id} value={s.id}>
                                {s.label}
                              </SelectItem>
                            ))}
                            <SelectItem value="__none__">Other / no stage</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="mb-1 block text-xs text-muted-foreground">
                          Times to film
                        </Label>
                        <Select
                          value={subject.frequency.toString()}
                          onValueChange={(value) =>
                            updateCustomSubject(index, "frequency", parseInt(value))
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[1, 2, 3, 4, 5].map((n) => (
                              <SelectItem key={n} value={n.toString()}>
                                {n}x
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="mb-1 block text-xs text-muted-foreground">Duration</Label>
                        <Select
                          value={subject.duration.toString()}
                          onValueChange={(value) =>
                            updateCustomSubject(index, "duration", parseInt(value))
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="15">15 min</SelectItem>
                            <SelectItem value="30">30 min</SelectItem>
                            <SelectItem value="45">45 min</SelectItem>
                            <SelectItem value="60">60 min</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeCustomSubject(index)}
                        className="mt-5"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-3 gap-3 pl-1">
                      <div>
                        <Label className="text-xs text-muted-foreground">
                          Preferred time start (optional)
                        </Label>
                        <Input
                          type="time"
                          value={subject.preferredTimeStart || ""}
                          onChange={(e) =>
                            updateCustomSubject(index, "preferredTimeStart", e.target.value)
                          }
                          placeholder="e.g., 14:00"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">
                          Preferred time end (optional)
                        </Label>
                        <Input
                          type="time"
                          value={subject.preferredTimeEnd || ""}
                          onChange={(e) =>
                            updateCustomSubject(index, "preferredTimeEnd", e.target.value)
                          }
                          placeholder="e.g., 17:00"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">
                          Preferred cameraman (optional)
                        </Label>
                        <Select
                          value={subject.preferredCrewMember || "any"}
                          onValueChange={(value) =>
                            updateCustomSubject(
                              index,
                              "preferredCrewMember",
                              value === "any" ? "" : value,
                            )
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Any" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="any">Any crew member</SelectItem>
                            {crew
                              .filter((c) => activatedCrew.has(c.id!))
                              .map((c) => (
                                <SelectItem key={c.id} value={c.id!}>
                                  {c.name}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Default duration */}
            <div>
              <Label className="mb-3 block text-base font-semibold">
                Default filming duration per artist/subject
              </Label>
              <Select value={defaultDuration} onValueChange={setDefaultDuration}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="15">15 minutes</SelectItem>
                  <SelectItem value="30">30 minutes</SelectItem>
                  <SelectItem value="45">45 minutes</SelectItem>
                  <SelectItem value="60">60 minutes</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Travel time */}
            <div>
              <Label className="mb-3 block text-base font-semibold">
                Default travel time between stages
              </Label>
              <Select
                value={travelTime.toString()}
                onValueChange={(value) => setTravelTime(parseInt(value))}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="15">15 minutes</SelectItem>
                  <SelectItem value="30">30 minutes</SelectItem>
                  <SelectItem value="45">45 minutes</SelectItem>
                  <SelectItem value="60">60 minutes</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Crew break times */}
            <div>
              <div className="mb-3 flex items-center justify-between">
                <Label className="text-base font-semibold">Crew break times</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setCrewBreaks([...crewBreaks, { time: "18:00", duration: "30" }])}
                >
                  <Plus className="mr-1 h-4 w-4" />
                  Add Break
                </Button>
              </div>
              <div className="space-y-3">
                {crewBreaks.map((breakItem, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-[2fr_2fr_auto] items-center gap-3 rounded-lg bg-muted/50 p-3"
                  >
                    <div>
                      <Label className="mb-1 block text-xs text-muted-foreground">Break Time</Label>
                      <Input
                        type="time"
                        value={breakItem.time}
                        onChange={(e) => {
                          const newBreaks = [...crewBreaks];
                          newBreaks[index].time = e.target.value;
                          setCrewBreaks(newBreaks);
                        }}
                      />
                    </div>
                    <div>
                      <Label className="mb-1 block text-xs text-muted-foreground">Duration</Label>
                      <Select
                        value={breakItem.duration}
                        onValueChange={(value) => {
                          const newBreaks = [...crewBreaks];
                          newBreaks[index].duration = value;
                          setCrewBreaks(newBreaks);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="15">15 min</SelectItem>
                          <SelectItem value="30">30 min</SelectItem>
                          <SelectItem value="45">45 min</SelectItem>
                          <SelectItem value="60">60 min</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setCrewBreaks(crewBreaks.filter((_, i) => i !== index))}
                      className="mt-5"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </ScrollArea>

        <div className="flex justify-end gap-2 border-t pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isGenerating}>
            Cancel
          </Button>
          <Button onClick={handleGenerateClick} disabled={isGenerating}>
            <Sparkles className="mr-2 h-4 w-4" />
            Generate with AI
          </Button>
        </div>
      </DialogContent>

      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="text-warning h-5 w-5" />
              Please Review AI Output
            </AlertDialogTitle>
            <AlertDialogDescription>
              AI may make mistakes. Please double-check the generated planning before publishing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleGenerate}>
              I understand, generate now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AiProgressDialog open={showProgressDialog} progress={generationProgress} />

      <AiErrorDialog
        open={showErrorDialog}
        onOpenChange={setShowErrorDialog}
        errorMessage={errorMessage}
        suggestions={errorSuggestions}
        conflicts={errorConflicts}
        onSolve={(conflict) => {
          // Handle solve action
          if (conflict.solutionAction.type === "remove_custom_subject") {
            const { subject, location } = conflict.solutionAction.data;
            setCustomSubjects((prev) =>
              prev.filter((cs) => !(cs.subject === subject && cs.location === location)),
            );
            toast.success(`Removed custom subject: ${subject} at ${location}`);
            // Auto-retry generation
            setTimeout(() => handleGenerateClick(), 500);
          } else if (conflict.solutionAction.type === "remove_artist") {
            const { artist, stage } = conflict.solutionAction.data;
            const key = `${artist}-${stage}`;
            setActivatedArtists((prev) => {
              const newSet = new Set(prev);
              newSet.delete(key);
              return newSet;
            });
            toast.success(`Deactivated artist: ${artist}`);
            // Auto-retry generation
            setTimeout(() => handleGenerateClick(), 500);
          }
        }}
        onIgnore={(conflict) => {
          // Handle ignore action - deactivate the artist
          const { artist, stage } = conflict.solutionAction.data;
          const key = `${artist}-${stage}`;
          setActivatedArtists((prev) => {
            const newSet = new Set(prev);
            newSet.delete(key);
            return newSet;
          });
          toast.info(`Ignoring artist: ${artist} - will not be scheduled`);
          // Auto-retry generation
          setTimeout(() => handleGenerateClick(), 500);
        }}
      />
    </Dialog>
  );
}
